import { useEffect, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { TrendingUp, TrendingDown, Trophy, Target, Coins, Download, Printer } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import api from '../lib/api';

const CHANNELS = [
  { key: 'all',      label: 'Todos', color: '#10b981' },
  { key: 'free',     label: 'FREE',  color: '#3b82f6' },
  { key: 'vip',      label: 'VIP',   color: '#f59e0b' },
  { key: 'stakazos', label: 'Stakazos 10', color: '#ec4899' },
];

const PERIODS = [
  { days: 7,   label: '7 días'  },
  { days: 30,  label: '30 días' },
  { days: 90,  label: '90 días' },
];

const StatCard = ({ icon: Icon, label, value, tone = 'neutral' }) => {
  const toneClasses = {
    green:   'border-emerald-700/40 text-emerald-300',
    red:     'border-rose-700/40 text-rose-300',
    amber:   'border-amber-700/40 text-amber-300',
    neutral: 'border-zinc-800 text-zinc-100',
  };
  return (
    <div className={`bg-zinc-900/60 border rounded-xl p-4 flex items-center gap-3 ${toneClasses[tone]}`} data-testid={`stat-card-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="w-10 h-10 rounded-lg bg-zinc-800/70 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p>
        <p className="text-lg font-bold">{value}</p>
      </div>
    </div>
  );
};

const ResultBadge = ({ result }) => {
  const map = {
    GANADA:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    PERDIDA:   'bg-rose-500/15 text-rose-300 border-rose-500/40',
    PENDIENTE: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    ANULADA:   'bg-zinc-500/15 text-zinc-400 border-zinc-500/40',
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded font-semibold border ${map[result] || map.PENDIENTE}`}>
      {result}
    </span>
  );
};

// CSV utilities
const CSV_COLS = ['fecha', 'hora', 'pais', 'liga', 'partido', 'mercado', 'pronostico', 'cuota', 'stake', 'marcador', 'resultado', 'unidades'];
const CSV_HEADERS = ['Fecha', 'Hora', 'País', 'Liga', 'Partido', 'Mercado', 'Pronóstico', 'Cuota', 'Stake', 'Marcador', 'Resultado', 'Unidades'];
const _csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[,;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCSV = (rows, filename) => {
  const lines = [CSV_HEADERS.join(',')];
  rows.forEach(r => lines.push(CSV_COLS.map(c => _csvEscape(r[c])).join(',')));
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function WeeklyReport() {
  const [channel, setChannel] = useState('all');
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countryFilter, setCountryFilter] = useState('all');
  const [leagueFilter, setLeagueFilter] = useState('all');
  const [multiData, setMultiData] = useState(null);

  // Fetch main channel data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/weekly-report/details?channel=${channel}&days=${days}`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [channel, days]);

  // Fetch all 3 channels for the comparison chart (only when tab=all)
  useEffect(() => {
    if (channel !== 'all') { setMultiData(null); return; }
    let cancelled = false;
    Promise.all(['free', 'vip', 'stakazos'].map(ch =>
      api.get(`/admin/weekly-report/details?channel=${ch}&days=${days}`)
         .then(r => ({ ch, evo: r.data.evolution || [] }))
         .catch(() => ({ ch, evo: [] }))
    )).then(results => {
      if (cancelled) return;
      // Align by index: build a wide-format array [{ index, free: n, vip: n, stakazos: n }, ...]
      const maxLen = Math.max(...results.map(r => r.evo.length), 0);
      const merged = [];
      for (let i = 0; i < maxLen; i++) {
        const row = { index: i + 1 };
        results.forEach(r => {
          row[r.ch] = r.evo[i] ? r.evo[i].cumulative_units : null;
        });
        merged.push(row);
      }
      setMultiData(merged);
    });
    return () => { cancelled = true; };
  }, [channel, days]);

  const historial = data?.historial || [];
  const stats = data?.stats || {};

  // Derived filter options
  const countries = useMemo(() => {
    const set = new Set(historial.map(h => h.pais).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [historial]);
  const leagues = useMemo(() => {
    const set = new Set(historial
      .filter(h => countryFilter === 'all' || h.pais === countryFilter)
      .map(h => h.liga).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [historial, countryFilter]);

  const filtered = useMemo(() => historial.filter(h =>
    (countryFilter === 'all' || h.pais === countryFilter) &&
    (leagueFilter === 'all' || h.liga === leagueFilter)
  ), [historial, countryFilter, leagueFilter]);

  const chartData = data?.evolution || [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6 print:py-2">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold" data-testid="page-title">Resumen Semanal</h1>
            <p className="text-sm text-zinc-400">Rendimiento por canal · evolución + historial</p>
          </div>
          <div className="flex gap-2 print:hidden">
            {PERIODS.map(p => (
              <button
                key={p.days}
                onClick={() => setDays(p.days)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                  days === p.days
                    ? 'bg-emerald-600 border-emerald-500 text-white'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white'
                }`}
                data-testid={`period-${p.days}d`}
              >
                {p.label}
              </button>
            ))}
            <Button
              variant="outline" size="sm"
              className="border-zinc-700 text-zinc-300 hover:text-white"
              onClick={() => downloadCSV(filtered, `betdatalab-${channel}-${days}d-${new Date().toISOString().slice(0,10)}.csv`)}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button
              variant="outline" size="sm"
              className="border-zinc-700 text-zinc-300 hover:text-white"
              onClick={() => window.print()}
              data-testid="print-pdf-btn"
            >
              <Printer className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        <div className="print:hidden">
          <Tabs value={channel} onValueChange={setChannel}>
            <TabsList className="bg-zinc-900 border border-zinc-800">
              {CHANNELS.map(c => (
                <TabsTrigger key={c.key} value={c.key} data-testid={`tab-${c.key}`}>{c.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-zinc-100">Evolución del rendimiento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full" data-testid="evolution-chart">
              {loading ? (
                <div className="flex items-center justify-center h-full text-zinc-500 text-sm">Cargando…</div>
              ) : channel === 'all' && multiData && multiData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={multiData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="index" stroke="#71717a" fontSize={11} />
                    <YAxis stroke="#71717a" fontSize={11} label={{ value: 'Unidades', angle: -90, position: 'insideLeft', fill: '#52525b', fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, color: '#f4f4f5', fontSize: 12 }}
                      labelFormatter={(idx) => `Apuesta #${idx}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="free"     name="FREE"        stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="vip"      name="VIP"         stroke="#f59e0b" strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="stakazos" name="Stakazos 10" stroke="#ec4899" strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              ) : chartData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-zinc-500 text-sm">Sin picks verificados en este período</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="perfArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="index" stroke="#71717a" fontSize={11} label={{ value: 'Apuestas', position: 'insideBottom', offset: -5, fill: '#52525b', fontSize: 10 }} />
                    <YAxis stroke="#71717a" fontSize={11} label={{ value: 'Unidades', angle: -90, position: 'insideLeft', fill: '#52525b', fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, color: '#f4f4f5', fontSize: 12 }}
                      labelFormatter={(idx) => `Apuesta #${idx}`}
                      formatter={(v) => [`${v} u`, 'Cumulado']}
                    />
                    <Area type="monotone" dataKey="cumulative_units" stroke="#10b981" strokeWidth={2} fill="url(#perfArea)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
              <StatCard icon={TrendingUp}   label="Mejor racha ganadora"    value={stats.best_win_streak ?? 0}         tone="green" />
              <StatCard icon={TrendingDown} label="Mejor racha perdedora"   value={stats.best_loss_streak ?? 0}        tone="red"   />
              <StatCard icon={Trophy}       label="Apuesta máxima ganadora" value={`${(stats.max_win_bet ?? 0).toFixed(2)} u`}  tone="green" />
              <StatCard icon={Target}       label="Apuesta máxima perdedora" value={`${(stats.max_loss_bet ?? 0).toFixed(2)} u`} tone="red"   />
              <StatCard icon={Coins}        label="Media por apuesta"       value={`${(stats.avg_per_pick ?? 0) >= 0 ? '+' : ''}${(stats.avg_per_pick ?? 0).toFixed(2)} u`} tone="amber" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base font-semibold text-zinc-100">Historial de oportunidades</CardTitle>
            <div className="flex items-center gap-2 flex-wrap print:hidden">
              <select
                value={countryFilter}
                onChange={e => { setCountryFilter(e.target.value); setLeagueFilter('all'); }}
                className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs rounded px-2 py-1"
                data-testid="country-filter"
              >
                {countries.map(c => <option key={c} value={c}>{c === 'all' ? 'Todos los países' : c}</option>)}
              </select>
              <select
                value={leagueFilter}
                onChange={e => setLeagueFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs rounded px-2 py-1"
                data-testid="league-filter"
              >
                {leagues.map(l => <option key={l} value={l}>{l === 'all' ? 'Todas las ligas' : l}</option>)}
              </select>
              <Badge variant="outline" className="border-zinc-700 text-zinc-400">{filtered.length} picks</Badge>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="picks-table">
              <thead className="text-zinc-500 uppercase text-[10px]">
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-2 px-2">Fecha</th>
                  <th className="text-left py-2 px-2">Hora</th>
                  <th className="text-left py-2 px-2">País</th>
                  <th className="text-left py-2 px-2">Liga</th>
                  <th className="text-left py-2 px-2">Partido</th>
                  <th className="text-left py-2 px-2">Mercado</th>
                  <th className="text-left py-2 px-2">Pronóstico</th>
                  <th className="text-right py-2 px-2">Cuota</th>
                  <th className="text-right py-2 px-2">Stake</th>
                  <th className="text-center py-2 px-2">Marcador</th>
                  <th className="text-center py-2 px-2">Resultado</th>
                  <th className="text-right py-2 px-2">Unidades</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="12" className="text-center py-10 text-zinc-500">Cargando…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan="12" className="text-center py-10 text-zinc-500">Sin picks en este período</td></tr>
                )}
                {filtered.map((p, i) => (
                  <tr key={p.id || i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition">
                    <td className="py-2 px-2 text-zinc-400">{p.fecha}</td>
                    <td className="py-2 px-2 text-zinc-400">{p.hora}</td>
                    <td className="py-2 px-2 text-zinc-400">{p.pais}</td>
                    <td className="py-2 px-2 text-zinc-500">{p.liga}</td>
                    <td className="py-2 px-2 text-zinc-100 font-medium">{p.partido}</td>
                    <td className="py-2 px-2 text-zinc-400">{p.mercado}</td>
                    <td className="py-2 px-2 text-emerald-300 font-semibold">{p.pronostico}</td>
                    <td className="py-2 px-2 text-right font-mono text-zinc-100">{p.cuota.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right font-mono text-zinc-400">{p.stake.toFixed(2)}</td>
                    <td className="py-2 px-2 text-center text-zinc-100">{p.marcador}</td>
                    <td className="py-2 px-2 text-center"><ResultBadge result={p.resultado} /></td>
                    <td className={`py-2 px-2 text-right font-mono font-semibold ${p.unidades > 0 ? 'text-emerald-300' : p.unidades < 0 ? 'text-rose-300' : 'text-zinc-500'}`}>
                      {p.unidades !== null ? (p.unidades > 0 ? '+' : '') + p.unidades.toFixed(2) + ' u' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
